import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { withRouter } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/layout/layout";
import Sidebar from "./sidebar/sidebar";
import ChatHeading from "./conversation/heading";
import Messages from "./conversation/messages";
import MessageInput from "./conversation/input";
import { SocketContext } from "../../contexts/SocketContext";
import { MESSAGE_SENT, MESSAGE_RECIEVED, MESSAGE_READ, CONVERSATION_SELECTED } from "../../Events";
import classes from "./messageLayout.module.scss";
import AttachmentModal from './modals/attachment';
import EmployeeViewModal from "./modals/employeeView";
import GroupModal from "./modals/createGroup";
import prices from "../../assets/prices";

const MessageLayout = (props) => {
  //General assets
  const socket = useContext(SocketContext)[0];
  const user = useSelector(state => state.auth.user);
  const theme = useSelector(state => state.themeReducer.theme);
  const [loaded, setLoaded] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [conversationId, setConversationId] = useState(props.match.params.id);
  const [tier, setTier] = useState("solo");

  //Messaging options
  const [directMessageUsers, setDirectMessageUsers] = useState([]);
  const [conversationList, setConversationList] = useState([]);

  //Messages
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageList, setMessageList] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [groupCount, setGroupCount] = useState(0);

  //Modal Controls
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showEmployeeViewModal, setShowEmployeeViewModal] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const canDownloadConversation = user && user.role === 'Manager';

  const lastReadEmitRef = useRef({});

  const emitMessageRead = useCallback((conversation, lastMessageId = null) => {
    if (!socket || !conversation || !conversation._id) return;

    const conversationId = conversation._id.toString();
    const messageKey = lastMessageId ? lastMessageId.toString() : 'none';

    if (lastReadEmitRef.current[conversationId] === messageKey) return;

    lastReadEmitRef.current[conversationId] = messageKey;
    socket.emit(MESSAGE_READ, { _id: conversationId }, user.id);
  }, [socket, user.id]);

  const mergeReadDate = (conversation, readUserId, date) => {
    if (!conversation) return conversation;

    const readDates = conversation.read_dates ? conversation.read_dates.map((entry) => ({ ...entry })) : [];
    let updated = false;

    const updatedReadDates = readDates.map((entry) => {
      if (entry.id === readUserId) {
        updated = true;
        return { ...entry, date };
      }
      return entry;
    });

    if (!updated) {
      updatedReadDates.push({ id: readUserId, date });
    }

    return { ...conversation, read_dates: updatedReadDates };
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const downloadConversationCsv = () => {
    if (!canDownloadConversation || !selectedConversation || messageList.length === 0) return;

    const headers = ['Sender', 'Message', 'Date/Time'];
    const rows = messageList.map((message) => {
      let messageContent = '';
      if (message.type === 'text') {
        messageContent = message.message || '';
      } else {
        messageContent = message.file_name || message.file_location || '';
      }

      const normalizedRow = [
        message.sender_name || '',
        messageContent,
        message.date ? dayjs(message.date).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map((value) => `"${(value || '').replace(/"/g, '""')}"`);

      return normalizedRow.join(',');
    });

    const csvContent = [headers.map((header) => `"${header}"`).join(','), ...rows].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    let filenameBase = selectedConversation.name ? selectedConversation.name.replace(/\s+/g, '_') : 'conversation';
    if (selectedConversation.type === 'Group') filenameBase = `${filenameBase}_group`;
    const filename = `${filenameBase}.csv`;

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  //--------------------------General Setup-----------------------------------

  //Get all direct message users and conversations
  useEffect(() => {
    axios.get('/api/conversations/userlist')
      .then(userListResponse => {
        //console.log(userListResponse.data);
        let directMessageUsers = userListResponse.data.users;
        let conversations = userListResponse.data.conversations;

        // Adding role to direct conversations
        let newConversations = [];
        for (let conversation of conversations) {
          if (conversation.type === "Direct") {
            let userId = conversation.user_ids.filter((id) => id !== user.id)[0];
            for (const directUser of directMessageUsers) {
              if (directUser._id === userId) {
                newConversations.push({...conversation, role: directUser.role });
              }
            }
          } else {
            newConversations.push(conversation);
            setGroupCount(groupCount + 1);
          }
        }

        setDirectMessageUsers(directMessageUsers);
        setConversationList(newConversations);
        //console.log(userListResponse.data.conversations);
        setLoaded(true);
      })
      .catch(err => {
        //console.log(err)
      });
  }, [user]);

  //Creating Message socket events
  useEffect(() => {
    if (!socket) return;

    const listeners = [];

    conversationList.forEach((conversation) => {
      const messageEvent = `${MESSAGE_RECIEVED}-${conversation._id}`;
      const readEvent = `${MESSAGE_READ}-${conversation._id}`;

      const handleMessage = (message) => {
        setNewMessage(message);
      };

      const handleRead = (payload) => {
        if (!payload || !payload.conversation_id || !payload.user_id || !payload.date) return;

        setConversationList((prevConversations) => prevConversations.map((conv) => {
          if (conv._id === payload.conversation_id) {
            return mergeReadDate(conv, payload.user_id, payload.date);
          }
          return conv;
        }));

        setSelectedConversation((prevSelected) => {
          if (prevSelected && prevSelected._id === payload.conversation_id) {
            return mergeReadDate(prevSelected, payload.user_id, payload.date);
          }
          return prevSelected;
        });
      };

      socket.off(messageEvent);
      socket.on(messageEvent, handleMessage);
      listeners.push({ event: messageEvent, handler: handleMessage });

      socket.off(readEvent);
      socket.on(readEvent, handleRead);
      listeners.push({ event: readEvent, handler: handleRead });
    });

    return () => {
      listeners.forEach(({ event, handler }) => {
        socket.off(event, handler);
      });
    };
  }, [conversationList, socket]);

  //Adding a new message to a conversation
  useEffect(() => {
    if (newMessage !== "" && selectedConversation) {
      if (selectedConversation._id === newMessage.conversation_id) {
        let newMessageList = [...messageList];
        newMessageList.push(newMessage);
        setMessageList(newMessageList);
        setNewMessage("");
        if (newMessage.sender_id !== user.id) {
          emitMessageRead(selectedConversation, newMessage._id);
        }
      } else {
        //console.log("Here");
        // Increment unread_count for conversation
        let newConversationList = [];
        for (const conversation of conversationList) {
          if (conversation._id === newMessage.conversation_id) {
            //console.log(conversation.unread_count+1);
            newConversationList.push({...conversation, unread_count: conversation.unread_count+1});
          } else newConversationList.push(conversation);
        }
        setConversationList(newConversationList);
        setNewMessage("");
      }
    }
  }, [newMessage, messageList, selectedConversation, conversationList, emitMessageRead, user.id]);

  //Setting selectedConversation in the backend for notifications
  useEffect(() => {
    if (socket !== null && selectedConversation !== null) {
      socket.emit(CONVERSATION_SELECTED, user.id, selectedConversation);
    }
  }, [selectedConversation, socket, user]);

  //For message notification clicks
  useEffect(() => {
    if (firstLoad || (props.match.params.id !== conversationId)) {
      if (props.match.params.id) {
        conversationList.forEach((conversation) => {
          if (conversation._id === props.match.params.id) {
            setSelectedConversation(conversation);
            getMessages(conversation._id);
            setFirstLoad(false);
            setConversationId(props.match.params.id);
          }
        });
      }
    } else {
      setFirstLoad(false);
    }
  }, [conversationList, props.match.params.id]);

  useEffect(() => {
      axios.get("/api/payment/manager")
        .then(response => {
          if (response.data.price_id == prices.solo_monthly || response.data.price_id == prices.solo_yearly)
              setTier("solo");
          else if (response.data.price_id == prices.agency_monthly || response.data.price_id == prices.agency_yearly)
              setTier("agency");

        })
        .catch(err => {
          //console.log(err);
          setTier("error");
        })
  }, [])

  const goBack = (e) => {
    setSelectedConversation(null);
    socket.emit(CONVERSATION_SELECTED, user.id, null);
    props.history.push("/dashboard");
  }

  const selectConversation = (conversation_id) => {
    conversationList.forEach((conversation) => {
      if (conversation._id === conversation_id) {
        setSelectedConversation(conversation);
        getMessages(conversation._id);
        props.history.push(`/messages/${conversation._id}`);
      }
    });
  }

  const createConversation = (params) => {
    axios.post('/api/conversations/', params)
      .then(response => {
        //console.log(response.data);
        const newConversationList = [...conversationList];
        newConversationList.push(response.data);
        setConversationList(newConversationList);
        return(response.data);
      })
      .catch(err => {
        //console.log(err);
        return null;
      });
  }

  const getMessages = async (conversation_id) => {
    try {
      const response = await axios.get(`/api/messages/${conversation_id}`);
      setMessageList(response.data);
      if (window.innerWidth <= 768) toggleSidebar();

      const existingConversation = conversationList.find((conversation) => conversation._id === conversation_id);
      let refreshedConversation = null;

      try {
        const refreshedResponse = await axios.get(`/api/conversations/${conversation_id}`);
        refreshedConversation = refreshedResponse.data;
      } catch (err) {
        //console.log(err);
      }

      const baseConversation = refreshedConversation ? { ...refreshedConversation } : existingConversation ? { ...existingConversation } : null;

      if (!baseConversation) return;

      if (existingConversation && existingConversation.role && !baseConversation.role) {
        baseConversation.role = existingConversation.role;
      }

      const conversationPayload = { ...baseConversation, unread_count: 0 };

      setConversationList((prevConversations) => {
        let found = false;
        const updated = prevConversations.map((conversation) => {
          if (conversation._id === conversation_id) {
            found = true;
            return conversationPayload;
          }
          return conversation;
        });
        return found ? updated : [...updated, conversationPayload];
      });

      setSelectedConversation(conversationPayload);

    } catch (err) {
      //console.log(err);
    }
  }

  const sendMessage = (e, message, setMessage) => {
    e.preventDefault();
    //console.log("Selected Conversation: ", selectedConversation);
    if (selectedConversation && message !== "") {
      const params = {
        conversation_id: selectedConversation._id,
        message: message,
        type: 'text'
      }
      axios.post('/api/messages/text', params)
        .then(response => {
          //console.log("Response: ", response.data);
          setMessage("");
          socket.emit(MESSAGE_SENT, selectedConversation, response.data);
        })
        .catch(err => {
          //console.log(err);
        });
    } else if (message === "") {
      //console.log("Please type a message");
    } else {
      //console.log("No conversation selected");
    }
  }

  const addGroup = (e) => {
    if (groupCount >= 10 && tier == "solo")
      alert("You can't add more than 10 groups on the Solo tier. Upgrade to Agency to add more.");
    else
      setShowCreateGroup(true);
  }

  //-----------------------Add/Remove user to a group message--------------------
  const [addUserToGroupError, setAddUserToGroupError] = useState("");

  const addUserToGroupClick = (e, newUser, setUser) => {
    if (newUser === "") {
      setAddUserToGroupError("Select a User");
    } else {
      let userToAdd = null;
      directMessageUsers.forEach((user) => {
        if (user.name === newUser) {
          userToAdd = user;
        }
      });
      if (userToAdd) {
        if (!selectedConversation.user_ids.includes(userToAdd._id.toString())) {
          const newGroupIds = [...selectedConversation.user_ids];
          newGroupIds.push(userToAdd._id.toString());
          const newGroupNames = [...selectedConversation.display_names];
          newGroupNames.push(newUser);
          const newReadDates = [...selectedConversation.read_dates];
          newReadDates.push({ id: userToAdd._id.toString(), date: new Date() });
          const params = {
            id: selectedConversation._id,
            user_ids: newGroupIds,
            display_names: newGroupNames,
            read_dates: newReadDates
          };
          //Update conversation
          axios.put('/api/conversations/add/user', params)
            .then(response => {
              setSelectedConversation(response.data);
              let replaceIndex = null;
              conversationList.forEach((conversation, index) => {
                if (conversation._id === response.data._id) {
                  replaceIndex = index;
                }
              });
              if (replaceIndex) {
                let newConversationList = [...conversationList];
                newConversationList[replaceIndex] = response.data;
                setConversationList(newConversationList);
              }
              setUser("");
              setAddUserToGroupError("");
            })
            .catch(err => {
              //console.log(err);
            });
        } else {
          setUser("");
        }
      } else {
        setAddUserToGroupError("User not found");
      }
    }
  }

  const removeGroupMessageUser = (e, userName) => {
    let userToRemove = null;
    directMessageUsers.forEach((user) => {
      if (user.name === userName) {
        userToRemove = user;
      }
    });
    if (userToRemove) {
      let params = {
        id: selectedConversation._id,
        user_id: userToRemove._id,
        name: userName
      };
      axios.put('/api/conversations/remove/user', params)
        .then(response => {
          let newConversations = [...conversationList];
          let replaceIndex = null;
          conversationList.forEach((conversation, index) => {
            if (conversation._id === response.data._id) {
              replaceIndex = index;
            }
          });
          setSelectedConversation(response.data);
          newConversations[replaceIndex] = response.data;
          setConversationList(newConversations);
        })
        .catch(err => {
          //console.log(err);
        });
    }
  }

  //-------------------------Leave Group Message--------------------------
  const leaveGroupMessage = (e) => {
    let params = {
      id: selectedConversation._id,
      user_id: user.id,
      name: user.name
    };
    axios.put('/api/conversations/remove/user', params)
      .then(response => {
        let newConversations = [...conversationList];
        let removeIndex = null;
        conversationList.forEach((conversation, index) => {
          if (conversation._id === selectedConversation._id) {
            removeIndex = index;
          }
        });
        newConversations.splice(removeIndex, 1);
        setConversationList(newConversations);
        setSelectedConversation(null);
        setMessageList([]);
        props.history.push('/messages');
      })
      .catch(err => {
        //console.log(err);
      });
  }

  //-----------------------Delete Group Message----------------------------
  const deleteGroupConversation = (e, id) => {
    axios.delete(`/api/conversations/${id}`)
      .then(response => {
        props.history.push('/messages');
        setSelectedConversation(null);
        setMessageList([]);
        let newConversations = [...conversationList];
        let removeIndex = null;
        conversationList.forEach((conversation, index) => {
          if (conversation._id === id) {
            removeIndex = index;
          }
        });
        newConversations.splice(removeIndex, 1);
        setConversationList(newConversations);
      })
      .catch(err => {
        //console.log(err);
      });
  }

  const showSidebar = (e) => {
    setCollapsed(!collapsed);
  }

  //---------------------------Messages scroll----------------------------

  const messageContainer = useRef(null);

  const scrollToMyRef = () => {
    //console.log("Scroll height: ", messageContainer.current.scrollHeight);
    //console.log("Client height: ", messageContainer.current.clientHeight);
    const scroll = messageContainer.current.scrollHeight - messageContainer.current.clientHeight;
    messageContainer.current.scrollTo(0, scroll);
  }

  useEffect(() => {
    window.scrollTo(0, 1);
    if (loaded) scrollToMyRef();
    document.body.style.overflow = "hidden"
  }, [messageList, messageContainer, loaded]);

  useEffect(() => {
    if (!selectedConversation) return;

    const lastMessage = messageList.length > 0 ? messageList[messageList.length - 1] : null;
    emitMessageRead(selectedConversation, lastMessage ? lastMessage._id : null);
  }, [emitMessageRead, selectedConversation, messageList]);

  const readReceipt = useMemo(() => {
    if (!selectedConversation || messageList.length === 0) return null;

    const lastUserMessage = [...messageList].reverse().find((message) => message.sender_id === user.id);

    if (!lastUserMessage) return null;

    const lastMessageDate = dayjs(lastUserMessage.date);
    if (!lastMessageDate.isValid()) return null;

    const readEntries = (selectedConversation.read_dates || []).filter((entry) => {
      if (!entry || entry.id === user.id || !entry.date) return false;
      const readDate = dayjs(entry.date);
      if (!readDate.isValid()) return false;
      return !readDate.isBefore(lastMessageDate);
    });

    return {
      messageId: lastUserMessage._id,
      seen: readEntries.length > 0
    };
  }, [selectedConversation, messageList, user.id]);

  return (
    <Layout loaded={loaded} showSidebar={false} subscription={true}>
      <div className={`row ${classes.Content}`} style={{ margin: "0px" }}>

          {!collapsed && (
        <div className={`${classes.SidebarCol}`}>
          <Sidebar
            user={user}
            theme={theme}
            goBack={goBack}
            selected={selectedConversation}
            select={selectConversation}
            conversations={conversationList}
            createGroupToggle={(e) => addGroup()}
            employeeViewModal={(e) => setShowEmployeeViewModal(true)}
          />
        </div>
          )}

        <div className={`${classes.ConversationCol}`}>
          <div className={` ${collapsed ? classes.ConversationRowExpand : classes.ConversationRow}`}>
            <div className={`${classes.HeadingCol}`}>
              <ChatHeading
                user={user}
                theme={theme}
                conversation={selectedConversation}
                addUser={addUserToGroupClick}
                userList={directMessageUsers}
                addUserToGroupError={addUserToGroupError}
                setAddUserError={setAddUserToGroupError}
                leaveGroupMessage={leaveGroupMessage}
                removeGroupMessageUser={removeGroupMessageUser}
                deleteGroupConversation={deleteGroupConversation}
                showSidebar={showSidebar}
                downloadConversation={downloadConversationCsv}
                canDownload={canDownloadConversation}
              />
            </div>
            <div ref={messageContainer} className={`${collapsed ? classes.MessagesColExpand : classes.MessagesCol}`}>
              <Messages
                messages={messageList}
                user={user}
                theme={theme}
                readReceipt={readReceipt}
              />
            </div>
            <div className={`${collapsed ? classes.InputColExpand : classes.InputCol}`}>
              <MessageInput
                theme={theme}
                sendMessage={sendMessage}
                showModal={(e) => setShowAttachmentModal(true)}
              />
            </div>
          </div>
        </div>

        {/* File attachment modal */}
        <AttachmentModal
          show={showAttachmentModal}
          setShow={setShowAttachmentModal}
          user={user}
          theme={theme}
          socket={socket}
          conversation={selectedConversation}
        />

        {/* Create Group Modal */}
        <GroupModal
          show={showCreateGroup}
          setShow={setShowCreateGroup}
          user={user}
          theme={theme}
          createConversation={createConversation}
          messageUsers={directMessageUsers}
        />

        {/* EmployeeView Modal */}
        <EmployeeViewModal
          show={showEmployeeViewModal}
          setShow={setShowEmployeeViewModal}
          theme={theme}
          setConversation={setSelectedConversation}
          messageUsers={directMessageUsers}
        />

      </div>

    </Layout>
  );
}

export default withRouter(MessageLayout);
